import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'

import { useSelector } from 'react-redux'
import * as sessionUserSelector from 'selectors/sessionUser'

import {
  useFetchTicketsSubscriptionsQuery,
  useFetchTicketsViewsSubscriptionsQuery,
  useUnsubscribeToTicketsViewsMutation,
} from 'api/subscriptions'

import {
  useLocationQueryParams,
  useSetSearchParamsWithQueryString,
} from 'utils/hooks'

import { useSortableColumnsOptions } from 'components/Shared/SortDropdown/hooks'

import ErrorPage404 from 'components/App/ErrorPage/ErrorPage404'
import MainWrap from 'components/Shared/Skeleton/MainWrap/Index'
import Content from 'components/Shared/Skeleton/Content/Index'
import Header from 'components/Shared/Header/Index'

import FilterPanel from 'components/Shared/Skeleton/FilterPanel/Index'
import FilterPanelTitle from 'components/Shared/Skeleton/FilterPanel/FilterPanelTitle'
import UnwrappableSection from 'components/Shared/Skeleton/FilterPanel/UnwrappableSection'
import FilterPanelSearch from 'components/Shared/Skeleton/FilterPanel/FilterPanelSearch'
import SortDropdown from 'components/Shared/SortDropdown/Index'
import ClearFiltersIcon from 'components/Shared/ClearFiltersIcon/Index'

import * as ticketingSystemsSelector from 'selectors/ticketingSystems'
import TicketsSubscriptionsTable from './TicketsSubscriptionsTable'
import TicketsViewsSubscriptionsTable from './TicketsViewsSubscriptionsTable'
import SubscriptionTypePanel from './SubscriptionTypePanel'

import CardsListPanel from 'components/Shared/CardsListPanel/Index'
import RowsCountAndPagination from 'components/Shared/RowsCountAndPagination/Index'
import Tooltip from 'components/Shared/Tooltip/Index'

import { RouteSubscriptionsViews, RouteSubscriptionsTickets } from 'routes'

const TICKETS_SUBSCRIPTION_TYPE_ID = 'tickets'
const VIEWS_SUBSCRIPTION_TYPE_ID = 'views'

const SubscriptionsPage = () => {
  const { t } = useTranslation()
  const params = useParams()
  const navigate = useNavigate()
  const [locationQueryParams, location] = useLocationQueryParams()

  const userIsAdmin = useSelector(sessionUserSelector.isAdmin)
  const userIsPower = useSelector(sessionUserSelector.isPowerUser)
  const activePage = parseInt(locationQueryParams.page) || 1

  const sortField = locationQueryParams.sort_field || 'created_at'
  const sortDir = locationQueryParams.sort_dir || 'desc'

  const searchValue = locationQueryParams.search || ''
  const ticketingSystems = useSelector(ticketingSystemsSelector.ticketingSystems)
  const setQueryParam = useSetSearchParamsWithQueryString()

  const selectedTicketingSystemDisplayId = params.ticketingSystemDisplayId || ''
  const selectedTicketingSystem = ticketingSystems.find(ts => ts.displayId === selectedTicketingSystemDisplayId)

  const ticketingSystemHasOnlyOneBoard = selectedTicketingSystem.providerTicketBoards.length === 1

  const isRedirectToErrorPage = userIsAdmin || userIsPower || !selectedTicketingSystem

  const [removeTicketsViewsSubscription, { isLoading: isRemoving }] = useUnsubscribeToTicketsViewsMutation()

  const selectedSubscriptionTypeId =
    RouteSubscriptionsViews.matchUrlExact(location.pathname) ? VIEWS_SUBSCRIPTION_TYPE_ID : TICKETS_SUBSCRIPTION_TYPE_ID

  const isTicketsSubscriptionSelected = selectedSubscriptionTypeId === TICKETS_SUBSCRIPTION_TYPE_ID


  const routeBySubscriptionType = (subscriptionType, ticketingSystemDisplayId = selectedTicketingSystemDisplayId) => {
    if (subscriptionType === TICKETS_SUBSCRIPTION_TYPE_ID) {
      return RouteSubscriptionsTickets({ ticketingSystemDisplayId: ticketingSystemDisplayId })
    }

    return RouteSubscriptionsViews({ ticketingSystemDisplayId: ticketingSystemDisplayId })
  }

  const onTicketingSystemSelected = (ticketingSystem) => {
    navigate({
      pathname: routeBySubscriptionType(selectedSubscriptionTypeId, ticketingSystem.displayId),
    })
  }
  const getDefaultUrlParams = () => {
    const params = {
      page: 1,
      search: searchValue,
      sort_field: sortField,
      sort_dir: sortDir,
    }

    return params
  }

  const updateURL = (params) => {
    const newParams = {
      ...getDefaultUrlParams(),
      ...params,
    }

    setQueryParam(newParams)
  }

  const onSearch = (newSearchValue) => {
    updateURL({
      search: newSearchValue,
    })
  }

  const { data: viewsSubscriptionsData, isFetching: isViewsSubscriptionFetching } =
    useFetchTicketsViewsSubscriptionsQuery({
      ticketingSystemDisplayId: selectedTicketingSystemDisplayId,
      params: {
        page: activePage,
        searchValue: searchValue,
        sortField: sortField,
        sortDir: sortDir,
      },
    }, { skip: isTicketsSubscriptionSelected })

  const viewsSubscriptionsList = viewsSubscriptionsData?.subscriptions || []
  const viewsSubscriptionsTotalCount = viewsSubscriptionsData?.metaData?.totalCount || 0
  const viewsSubscriptionsTotalPages = viewsSubscriptionsData?.metaData?.totalPages || 1
  const viewsSubscriptionsSortableColumns = viewsSubscriptionsData?.metaData?.sortableColumns || []


  const { data: ticketsSubscriptionsData, isFetching: isTicketsSubscriptionFetching } =
    useFetchTicketsSubscriptionsQuery({
      ticketingSystemDisplayId: selectedTicketingSystemDisplayId,
      params: {
        page: activePage,
        searchValue: searchValue,
        sortField: sortField,
        sortDir: sortDir,
      },
    }, { skip: !isTicketsSubscriptionSelected })


  const ticketsSubscriptionsList = ticketsSubscriptionsData?.subscriptions || []
  const ticketsSubscriptionsTotalCount = ticketsSubscriptionsData?.metaData?.totalCount || 0
  const ticketsSubscriptionsTotalPages = ticketsSubscriptionsData?.metaData?.totalPages || 1
  const ticketsSubscriptionsSortableColumns = ticketsSubscriptionsData?.metaData?.sortableColumns || []

  const subscriptionsList = isTicketsSubscriptionSelected ? ticketsSubscriptionsList : viewsSubscriptionsList
  const totalCount = isTicketsSubscriptionSelected ? ticketsSubscriptionsTotalCount : viewsSubscriptionsTotalCount
  const totalPages = isTicketsSubscriptionSelected ? ticketsSubscriptionsTotalPages : viewsSubscriptionsTotalPages
  const sortableColumns = isTicketsSubscriptionSelected ? ticketsSubscriptionsSortableColumns : viewsSubscriptionsSortableColumns
  const isFetching = isViewsSubscriptionFetching || isTicketsSubscriptionFetching || isRemoving

  const sortableColumnsOptions = useSortableColumnsOptions(sortableColumns)

  const onSubscriptionTypeSelected = (subscriptionType) => {
    navigate({
      pathname: routeBySubscriptionType(subscriptionType),
    })
  }

  const getBreadcrumbs = () => {
    const breadcrumbsList = [{
      text: t('subscriptionsPage.header'),
    }]
    return breadcrumbsList
  }

  const onSortFieldChange = ({ sortField, sortDir }) => {
    updateURL({
      sort_field: sortField,
      sort_dir: sortDir,
    })
  }

  const isAnyActiveFiltersOrSearch = Boolean(searchValue)
  const dataTest = isTicketsSubscriptionSelected ? 'subscription-tickets-page-filter-panel' : 'subscription-tickets-views-page-filter-panel'
  const clearUrl = routeBySubscriptionType(selectedSubscriptionTypeId)

  const renderTicketsFiltersSection = () => {
    return (
      <FilterPanel dataTest={dataTest}>
        <FilterPanelTitle
          title={t('subscriptionsPage.subscriptionFilteredBy')}
          withoutIcon
        />

        <UnwrappableSection>
          <FilterPanelSearch
            key={searchValue}
            initialValue={searchValue}
            onChange={onSearch}
            placeholder={t('subscriptionsPage.subscribeToSearch')}
            buttonTooltip={t('subscriptionsPage.subscribeToSearch')}
            tooltipId='tooltip-filter-panel'
          />
          {isAnyActiveFiltersOrSearch && (
            <ClearFiltersIcon
              clearUrl={clearUrl}
              tooltipText={t('subscriptionsPage.clearFiltersLink')}
            />
          )}
          <div />
        </UnwrappableSection>

        <SortDropdown
          title={t('common.sortedBy')}
          onChange={onSortFieldChange}
          value={{ sortField: sortField, sortDir: sortDir }}
          options={sortableColumnsOptions}
          defaultSortField='created_at'
          defaultSortDir='desc'
          clearActionTooltip={t('subscriptionsPage.filterPanelClearSortingTooltip')}
        />
        <Tooltip id='tooltip-filter-panel' />
      </FilterPanel>
    )
  }

  const subscriptionsData = () => {
    return (
      <>
        <>
          <SubscriptionTypePanel
            selectSubscriptionType={onSubscriptionTypeSelected}
            selectedSubscriptionTypeId={selectedSubscriptionTypeId}
          />
          {renderTicketsFiltersSection()}
        </>
        {!isTicketsSubscriptionSelected && (
          <TicketsViewsSubscriptionsTable
            isFetching={isFetching}
            subscriptions={subscriptionsList}
            removeTicketsViewsSubscription={removeTicketsViewsSubscription}
            ticketingSystemHasOnlyOneBoard={ticketingSystemHasOnlyOneBoard}
          />
        )}
        {isTicketsSubscriptionSelected && (
          <TicketsSubscriptionsTable
            isFetching={isFetching}
            subscriptions={subscriptionsList}
          />
        )}
      </>
    )
  }

  if (isRedirectToErrorPage) {
    return <ErrorPage404 />
  }

  return (
    <MainWrap pageTitle={t('subscriptionsPage.header')}>
      <Content loading={isFetching}>
        <Header
          title={t('subscriptionsPage.header')}
          breadcrumbsList={getBreadcrumbs()}
        />
        <CardsListPanel
          onSelect={onTicketingSystemSelected}
          cards={ticketingSystems}
          selectedCardId={selectedTicketingSystem.id}
          noPadding
        />
        {subscriptionsData()}

        <RowsCountAndPagination
          countText={t('subscriptionsPage.subscriptionsCount', { count: totalCount })}
          activePage={activePage}
          totalPages={totalPages}
          isFetching={isFetching}
          dataTest='subscriptions-count'
        />
      </Content>
    </MainWrap>
  )
}

export default SubscriptionsPage
